import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "@athena/ui/lib/utils";

/**
 * A single choice drawn as a row of pills — "How did you connect?", "When?".
 *
 * A radio group underneath rather than a row of toggle buttons, so arrow keys move between the
 * options, one tab stop covers the row, and a screen reader announces "3 of 6" the way it would
 * for any other exclusive choice. The pills are only the paint.
 *
 * `accent` fills the selected pill with the secondary color instead of the primary one, for a
 * second choice in the same form that should read as a different question (the mockups use it
 * for outcomes, beside channels in the primary fill).
 */
export interface ChoiceChipsOption {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface ChoiceChipsProps extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    "children" | "orientation"
> {
    options: ChoiceChipsOption[];
    accent?: boolean;
}

const ChoiceChips = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, ChoiceChipsProps>(
    ({ className, options, accent = false, ...props }, ref) => (
        <RadioGroupPrimitive.Root
            ref={ref}
            orientation="horizontal"
            className={cn("flex flex-wrap gap-2", className)}
            {...props}
        >
            {options.map((option) => (
                <RadioGroupPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={cn(
                        "border-border bg-card text-text-body inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm whitespace-nowrap transition-colors",
                        "hover:bg-accent focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                        "disabled:cursor-not-allowed disabled:line-through disabled:opacity-50",
                        "data-[state=checked]:font-semibold",
                        accent
                            ? "data-[state=checked]:border-secondary data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground"
                            : "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
                    )}
                >
                    {option.label}
                </RadioGroupPrimitive.Item>
            ))}
        </RadioGroupPrimitive.Root>
    ),
);
ChoiceChips.displayName = "ChoiceChips";

export { ChoiceChips };
